import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, Zap, Shield, Truck, Package, Minus, Plus, Check, Bell } from 'lucide-react';
import { useState } from 'react';
import { getStrengthLabel, getStrengthColor } from '../data/products';
import { useProducts } from '../context/ProductsContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { products as productsApi } from '../api/client';
import { useTheme } from '../context/ThemeContext';
import ProductCard from '../components/ProductCard';
import SEO from '../components/SEO';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { brandByName } from '@shared/brands';

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addToCart, state: cartState } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [notifyState, setNotifyState] = useState<'idle' | 'sending' | 'done'>('idle');
  const { products, isLoading } = useProducts();

  const { isDark } = useTheme();

  if (isLoading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-950' : ''}`}>
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const product = products.find((p) => p.id === id);
  if (!product) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-gray-950' : ''}`}>
        <div className="text-center">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>Product Not Found</h2>
          <Link to="/store" className="text-emerald-600 font-medium hover:underline">
            Back to Store
          </Link>
        </div>
      </div>
    );
  }

  const related = products
    .filter((p) => p.id !== product.id && p.inStock && (p.brand === product.brand || p.category === product.category))
    .slice(0, 8);

  const handleAddToCart = () => {
    const stock = product.stockQuantity ?? 100;
    const inCart = cartState.items.find((i) => i.product.id === product.id)?.quantity ?? 0;
    const canAdd = Math.min(qty, stock - inCart);
    if (canAdd <= 0) return;
    for (let i = 0; i < canAdd; i++) {
      addToCart(product);
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const strengthBars = product.strength <= 4 ? 1 : product.strength <= 8 ? 2 : product.strength <= 12 ? 3 : product.strength <= 20 ? 4 : 5;

  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: `https://zerosmoke.in${product.image}`,
    brand: { '@type': 'Brand', name: product.brand },
    sku: product.id,
    offers: {
      '@type': 'Offer',
      url: `https://zerosmoke.in/product/${product.id}`,
      priceCurrency: 'INR',
      price: product.price,
      availability: 'https://schema.org/InStock',
      seller: { '@type': 'Organization', name: 'ZeroSmoke' },
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        applicableCountry: 'IN',
        returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 7,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/ReturnFeesCustomerResponsibility',
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: product.price >= 5000 ? 0 : 199,
          currency: 'INR',
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'IN',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 2,
            unitCode: 'DAY',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 3,
            maxValue: 10,
            unitCode: 'DAY',
          },
        },
      },
    },
    ...(product.rating > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviewCount || 1,
        bestRating: 5,
      },
    }),
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: 'Home', url: '/' },
    { name: 'Store', url: '/store' },
    { name: product.name, url: `/product/${product.id}` },
  ]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        title={`${product.name} — Buy ${product.brand} Nicotine Pouches Online | ZeroSmoke.in`}
        description={`Buy ${product.name} online at ₹${product.price}. ${product.description} ${product.strength}mg strength, ${product.pouchesPerCan} pouches per can. 100% authentic ${product.brand} nicotine pouch. Free shipping on ₹5,000+. ZeroSmoke.in`}
        canonical={`/product/${product.id}`}
        ogImage={`https://zerosmoke.in${product.image}`}
        ogType="product"
        productPrice={product.price}
        productCurrency="INR"
        imageWidth={1200}
        imageHeight={1200}
        jsonLd={[productJsonLd, breadcrumbJsonLd]}
      />
      {/* Breadcrumb */}
      <div className={`border-b transition-colors duration-300 ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>Home</Link>
            <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>/</span>
            <Link to="/store" className={`${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>Store</Link>
            <span className={isDark ? 'text-gray-700' : 'text-gray-300'}>/</span>
            <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.name}</span>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className={`rounded-3xl p-8 border flex items-center justify-center min-h-[400px] ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-100'}`}
            style={{ background: `linear-gradient(135deg, ${product.colorAccent}08, ${product.colorAccent}12)` }}
          >
            <img
              src={product.image}
              alt={`${product.name} nicotine pouches — ${product.flavor} flavour, ${product.strength}mg, ${product.pouchesPerCan} pouches per can`}
              className="w-72 h-72 object-contain drop-shadow-2xl"
            />
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {(() => {
                const brandInfo = brandByName[product.brand];
                const badgeClass = "text-sm font-semibold px-3 py-1 rounded-full transition-opacity hover:opacity-80";
                const badgeStyle = { color: product.colorAccent, backgroundColor: product.colorAccent + '15' };
                return brandInfo ? (
                  <Link
                    to={`/brand/${brandInfo.slug}`}
                    className={badgeClass}
                    style={badgeStyle}
                    aria-label={`Browse all ${product.brand} products`}
                  >
                    {product.brand}
                  </Link>
                ) : (
                  <span className={badgeClass} style={badgeStyle}>{product.brand}</span>
                );
              })()}
              {product.isNew && (
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold rounded-full">NEW</span>
              )}
              {product.bestSeller && (
                <span className="px-3 py-1 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">BESTSELLER</span>
              )}
              {!product.inStock && (
                <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded-full">OUT OF STOCK</span>
              )}
            </div>

            <h1 className={`text-2xl sm:text-3xl font-extrabold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center gap-1">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    size={16}
                    fill={i < Math.round(product.rating) ? '#f59e0b' : isDark ? '#374151' : '#e5e7eb'}
                    stroke={i < Math.round(product.rating) ? '#f59e0b' : isDark ? '#374151' : '#e5e7eb'}
                  />
                ))}
              </div>
              <span className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{product.rating}</span>
              <span className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>({product.reviewCount} reviews)</span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-6">
              <span className={`text-3xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ₹{product.price.toLocaleString('en-IN')}
              </span>
              {product.originalPrice && (
                <>
                  <span className="text-xl text-gray-400 line-through">
                    ₹{product.originalPrice.toLocaleString('en-IN')}
                  </span>
                  <span className="text-sm font-bold text-red-500 bg-red-50 px-2 py-0.5 rounded">
                    {Math.round((1 - product.price / product.originalPrice) * 100)}% OFF
                  </span>
                </>
              )}
            </div>

            <p className={`leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {product.longDescription || product.description}
            </p>

            {/* Specs */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Zap size={14} style={{ color: getStrengthColor(product.strength) }} />
                  <span className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Strength</span>
                </div>
                <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.strength}{product.strengthUnit}</div>
                <div className="flex items-center gap-1 mt-1">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-1.5 w-6 rounded-full"
                      style={{
                        backgroundColor: i < strengthBars ? getStrengthColor(product.strength) : isDark ? '#374151' : '#e5e7eb',
                      }}
                    />
                  ))}
                </div>
                <span className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{getStrengthLabel(product.strength)}</span>
              </div>
              <div className={`rounded-xl p-4 ${isDark ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <div className="flex items-center gap-2 mb-1">
                  <Package size={14} className={isDark ? 'text-gray-500' : 'text-gray-400'} />
                  <span className={`text-xs uppercase ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Format</span>
                </div>
                <div className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{product.format}</div>
                <div className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{product.pouchesPerCan} pouches/can</div>
              </div>
            </div>

            {/* Add to Cart */}
            {product.inStock && (() => {
              const stock = product.stockQuantity ?? 100;
              const inCart = cartState.items.find((i) => i.product.id === product.id)?.quantity ?? 0;
              const remaining = stock - inCart;
              const maxQty = product.maxOrderQtyEnabled ? Math.min(product.maxOrderQty ?? 10, remaining) : remaining;
              const atLimit = remaining <= 0;
              return (
              <>
              {stock > 0 && stock < 10 && (
                <p className="text-sm font-semibold text-red-500 mb-2">Hurry, only {stock} items left!</p>
              )}
              {atLimit && (
                <p className={`text-sm font-medium mb-2 ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>You've added all available stock to your cart</p>
              )}
              <div className="flex items-center gap-4 mb-6">
                <div className={`flex items-center gap-3 rounded-xl p-1 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center border ${isDark ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  >
                    <Minus size={16} />
                  </button>
                  <span className={`w-8 text-center font-bold ${isDark ? 'text-white' : ''}`}>{qty}</span>
                  <button
                    onClick={() => {
                      setQty(Math.min(qty + 1, Math.max(maxQty, 1)));
                    }}
                    disabled={qty >= maxQty}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center border ${qty >= maxQty ? 'opacity-50 cursor-not-allowed' : ''} ${isDark ? 'bg-gray-700 border-gray-600 text-white hover:bg-gray-600' : 'bg-white border-gray-200 hover:bg-gray-50'}`}
                  >
                    <Plus size={16} />
                  </button>
                </div>
                <button
                  onClick={handleAddToCart}
                  disabled={atLimit}
                  className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-xl font-semibold text-white transition-all ${
                    atLimit
                      ? 'bg-gray-400 cursor-not-allowed opacity-60'
                      : added
                        ? 'bg-green-500'
                        : 'bg-emerald-600 hover:bg-emerald-700 hover:shadow-lg hover:shadow-emerald-600/25 active:scale-[0.98]'
                  }`}
                >
                  {added ? (
                    <>
                      <Check size={18} /> Added!
                    </>
                  ) : (
                    <>
                      <ShoppingCart size={18} /> Add to Cart {'\u2014'} {'\u20B9'}{(product.price * qty).toLocaleString('en-IN')}
                    </>
                  )}
                </button>
              </div>
            {product.inStock && product.maxOrderQtyEnabled && (
              <p className={`text-xs mb-4 -mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                Max {product.maxOrderQty} per order
              </p>
            )}
              </>
              );
            })()}

            {!product.inStock && (
              <div className={`rounded-xl mb-6 overflow-hidden ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div className={`p-4 text-center font-medium ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  This product is currently out of stock.
                </div>
                {!user ? (
                  <button
                    onClick={() => navigate('/login')}
                    className={`w-full flex items-center justify-center gap-2 py-3 font-semibold text-sm transition-all border-t ${
                      isDark ? 'bg-gray-700/50 text-amber-400 hover:bg-gray-700 border-gray-700' : 'bg-amber-50 text-amber-600 hover:bg-amber-100 border-gray-200'
                    }`}
                  >
                    <Bell size={16} /> Login to Get Notified
                  </button>
                ) : (
                  <button
                    onClick={async () => {
                      setNotifyState('sending');
                      try {
                        await productsApi.notifyWhenAvailable(product.id, user.email);
                        setNotifyState('done');
                      } catch {
                        setNotifyState('idle');
                      }
                    }}
                    disabled={notifyState !== 'idle'}
                    className={`w-full flex items-center justify-center gap-2 py-3 font-semibold text-sm transition-all border-t ${
                      notifyState === 'done'
                        ? isDark ? 'bg-emerald-500/10 text-emerald-400 border-gray-700' : 'bg-emerald-50 text-emerald-600 border-gray-200'
                        : isDark ? 'bg-gray-700/50 text-amber-400 hover:bg-gray-700 border-gray-700' : 'bg-amber-50 text-amber-600 hover:bg-amber-100 border-gray-200'
                    }`}
                  >
                    {notifyState === 'done' ? <><Check size={16} /> We'll notify you!</> : notifyState === 'sending' ? <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full" /> : <><Bell size={16} /> Notify Me When Available</>}
                  </button>
                )}
              </div>
            )}

            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: <Truck size={16} />, text: 'Free shipping ₹5,000+' },
                { icon: <Shield size={16} />, text: '100% Authentic' },
                { icon: <Package size={16} />, text: 'Discreet Packaging' },
              ].map((badge) => (
                <div key={badge.text} className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  <span className="text-emerald-600">{badge.icon}</span>
                  {badge.text}
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Related Products */}
        {related.length > 0 && (
          <div className="mt-20">
            <h2 className={`text-2xl font-extrabold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>You May Also Like</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {related.map((p, i) => (
                <ProductCard key={p.id} product={p} index={i} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
